"use client";
import React, { useContext, useEffect, useRef, useState, useMemo, useDeferredValue, useCallback, memo } from "react";
import { ChevronDown, Search, Tag } from "lucide-react";
import { useTranslation } from "@/app/i18n/client";
import ColorStyleContext from "@/lib/providers/color-style-privider";
// ⬇️ lazy load the chip to keep initial bundle lighter
import dynamic from "next/dynamic";
// Dynamic import (kept to reduce initial bundle) + manual prefetch on user intent
const ProductTag = dynamic(() => import(/* webpackPrefetch: true */ "../product-tag"), {
    ssr: false,
    loading: () => <div className="h-8 rounded-full bg-gray-100 animate-pulse px-6" />
});

import { Popover, PopoverContent, PopoverTrigger } from "@nextui-org/react";
import { TagWithFullRelation } from "@/util/query/product-query";
import { useRouter, useSearchParams } from "next/navigation";
import { borderRadiusShorthand, useLayoutStyle } from "@/lib/providers/layout-style-provider";
import { useKeyboardOpen } from "@/util/useKeyboardOpen";

// Progressive list rendering to avoid long blocking mount when there are many tags.
// Optimizations:
// 1) Render an initial larger chunk synchronously for instant visual feedback.
// 2) Use requestIdleCallback when available; fallback to rAF before setTimeout for smoother paint.
// 3) Reset only if the source items array identity changes (not on simple open/close toggles when disabled).
function useProgressiveList<T>(items: T[], enable: boolean, chunk = 24, delay = 16) {
    const [visible, setVisible] = useState<T[]>([]);
    const itemsRef = useRef(items);

    useEffect(() => {
        itemsRef.current = items;
    }, [items]);

    useEffect(() => {
        if (!enable) return;
        let i = 0;
        let cancelled = false;
        // Show first chunk immediately (larger for faster perceived load)
        i += chunk;
        setVisible(items.slice(0, Math.min(i, items.length)));

        const schedule = (cb: () => void) => {
            if ((window as any).requestIdleCallback) {
                (window as any).requestIdleCallback(cb, { timeout: 100 });
            } else if (typeof window !== 'undefined' && 'requestAnimationFrame' in window) {
                requestAnimationFrame(() => cb());
            } else {
                setTimeout(cb, delay);
            }
        };

        const pushChunk = () => {
            if (cancelled) return;
            i += chunk;
            if (i >= itemsRef.current.length) {
                setVisible(itemsRef.current);
                return;
            }
            setVisible(itemsRef.current.slice(0, Math.min(i, itemsRef.current.length)));
            schedule(pushChunk);
        };

        schedule(pushChunk);
        return () => { cancelled = true; };
    }, [enable, chunk, delay, items]);

    return visible;
}

type TagItemProps = {
        tag: TagWithFullRelation;
        isSelected: boolean;
        onToggle: (handle: string) => void;
};

const TagItem = memo(
    ({ tag, isSelected, onToggle }: TagItemProps) => {
        const handleClick = useCallback(
            (e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                onToggle(tag.handle);
            },
            [onToggle, tag.handle],
        );

        return (
            <ProductTag
                key={tag.id}
                tag={tag}
                nameOnly
                variant={isSelected ? "SOLID" : "FLAT"}
                className="cursor-pointer"
                onClick={handleClick}
            />
        );
    },
    // 2) Only re-render if THIS tag's selected state or identity changes
    (prev, next) => prev.tag.id === next.tag.id && prev.isSelected === next.isSelected,
);

TagItem.displayName = "Tag";

const CollectionListTagFilterClient = ({
    tags,
    paramName,
    pageParamKey = "page",
}: {
    tags: TagWithFullRelation[];
    paramName: string;
    pageParamKey?: string;
}) => {
    const { t } = useTranslation();
    const colorStyle = useContext(ColorStyleContext);
    const layoutStyle = useLayoutStyle();
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const searchParams = useSearchParams();
    const router = useRouter();
    const { isOpen: isKeyboardOpen, keyboardHeight } = useKeyboardOpen();

    const currentFilter = searchParams.get(paramName) || "";
    const selectedTagsFromParams = useMemo(
        () => (currentFilter ? currentFilter.split(",") : []),
        [currentFilter]
    );

    const [selectedTags, setSelectedTags] = useState(selectedTagsFromParams);
    const [isOpen, setIsOpen] = useState(false);
    const [skipReset, setSkipReset] = useState(false);

    // Search state (defer the actual value used for filtering)
    const [searchTerm, setSearchTerm] = useState("");
    const deferredSearch = useDeferredValue(searchTerm);

    const filteredTags = useMemo(() => {
        if (!deferredSearch) return tags;
        const q = deferredSearch.toLowerCase();
        return tags.filter(tag => tag.attributes.tagName.toLowerCase().includes(q));
    }, [tags, deferredSearch]);

    // Progressive list of tags only when panel is open
    const visibleTags = useProgressiveList(filteredTags, isOpen, 20);

    const SelectedTagCount = useMemo(
        () => (isOpen || skipReset ? selectedTags.length : selectedTagsFromParams.length),
        [isOpen, skipReset, selectedTags, selectedTagsFromParams]
    );

    const selectedSet = useMemo(() => new Set(selectedTags), [selectedTags]);

    // Prefetch the dynamic ProductTag component & warm up filtering upon user intent (hover/focus) BEFORE opening
    const prefetchRef = useRef(false);
    const prefetch = useCallback(() => {
        if (prefetchRef.current) return;
        prefetchRef.current = true;
        // Fire and forget - triggers the dynamic import chunk download early
        import("../product-tag");
    }, []);

    useEffect(() => {
        if (!isOpen) {
            if (skipReset) {
                const local = selectedTags.join(",");
                const fromParams = selectedTagsFromParams.join(",");
                if (local === fromParams) setSkipReset(false);
                return;
            }
            setSelectedTags(selectedTagsFromParams);
        }
    }, [isOpen, skipReset, selectedTags, selectedTagsFromParams]);

    const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    }, []);

    const toggleTag = useCallback((handle: string) => {
        setSelectedTags(prev =>
            prev.includes(handle) ? prev.filter(t => t !== handle) : [...prev, handle]
        );
    }, []);

    const handleApplyTags = useCallback(() => {
        setSkipReset(true);
        const params = new URLSearchParams(searchParams.toString());
        if (selectedTags.length > 0) params.set(paramName, selectedTags.join(","));
        else params.delete(paramName);
        params.delete(pageParamKey);

        router.push(`?${params.toString()}`, { scroll: false });
        setIsOpen(false);
        handleOnClose();
    }, [pageParamKey, paramName, router, searchParams, selectedTags]);

    const handleOnClose = useCallback(() => {
        if (searchInputRef.current) searchInputRef.current.value = "";
        setSearchTerm("");
    }, []);

    const clearFilter = useCallback(() => {
        setSelectedTags([]);
        handleOnClose();
    }, [handleOnClose]);

    // Memoize rendered tag elements to avoid re-mapping unless visible list or selection state changes
    const renderedTagList = useMemo(() => {
        if (visibleTags.length === 0) return null;
        return visibleTags.map(tag => (
            <TagItem
                key={tag.id}
                tag={tag}
                isSelected={selectedSet.has(tag.handle)}
                onToggle={toggleTag}
            />
        ));
    }, [visibleTags, selectedSet, toggleTag]);

    return (
        <Popover
            placement="bottom-end"
            shouldBlockScroll
            motionProps={{
                style: {
                    maxHeight: isKeyboardOpen
                        ? `calc(90vh - ${(keyboardHeight ?? 0)}px)`
                        : "inherit",
                },
            }}
            classNames={{
                content: "z-10 subpixel-antialiased outline-none box-border text-small flex flex-col items-stretch justify-start overflow-hidden bg-white shadow-md shadow-gray-500/40 border border-gray-200 rounded-md w-[min(500px,calc(100vw-1rem))] max-w-none p-0 max-h-[inherit]",
                base: "max-h-[inherit]",
            }}
            containerPadding={8}
            onOpenChange={(open) => setIsOpen(open)}
            isOpen={isOpen}
            onClose={handleOnClose}
        >
        <PopoverTrigger tabIndex={0}>
            <button
            type="button"
            className="bg-transparent border-input rounded-full gap-1 py-[3px] px-4 h-fit gap-x-2 justify-center border border-black flex items-center cursor-pointer focus:outline-none"
            style={{ color: colorStyle.text || "#3d3d3d" }}
            aria-label={t("product_list.filter_options.product_tags")}
            onPointerEnter={prefetch}
            onFocus={prefetch}
            >
            {t("product_list.filter_options.product_tags")}
            {SelectedTagCount > 0 && (
                <div
                className="px-2 text-sm rounded-full flex items-center justify-center dynamic-bg"
                style={{
                    "--dynamic-bg": colorStyle?.buttonBgNormal,
                    "--dynamic-hover-bg": colorStyle?.buttonBgNormal,
                    color: colorStyle?.buttonTextNormal,
                } as React.CSSProperties}
                >
                {SelectedTagCount}
                </div>
            )}
            <ChevronDown size={16} />
            </button>
        </PopoverTrigger>

        {isOpen && (
            <PopoverContent>
                {(titleprops) => (
                    <div className="flex flex-col w-full max-h-[inherit]">
                        <div className="flex-none p-4 border-b-gray-300 border-b w-full flex justify-between" {...titleprops}>
                            {t("product_list.filter_options.product_tags_filter")}
                            <button
                                className="text-sm text-gray-500 cursor-default"
                                style={
                                    selectedTags.length > 0
                                    ? { color: colorStyle.headingMain || "#111", cursor: "pointer" }
                                    : undefined
                                }
                                onClick={clearFilter}
                            >
                            {t("product_list.filter_options.clear_filter")}
                            </button>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto p-4 gap-6 flex flex-col">
                            <div className="flex items-center w-full py-2 px-4 bg-transparent border border-gray-300 rounded-full gap-2 color-gray-600">
                                <Search size={16} />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    aria-label="search tags"
                                    className="bg-transparent outline-none w-full"
                                    onChange={handleSearch}
                                    placeholder={t("product_list.filter_options.search_product_tags")}
                                />
                            </div>
                            <div className="flex flex-wrap gap-2 w-full">
                                {renderedTagList ? (
                                    renderedTagList
                                ) : (
                                    <div className="w-full text-center text-gray-500">
                                        {/* show skeletons briefly while progressive chunks fill in */}
                                        {deferredSearch || visibleTags.length === 0
                                            ? t("product_list.filter_options.no_tags_found")
                                            : <div className="flex gap-2 flex-wrap justify-center">
                                                {Array.from({ length: 12 }).map((_, i) => (
                                                    <div key={i} className="h-7 w-20 rounded-full bg-gray-100 animate-pulse" />
                                                ))}
                                            </div>
                                        }
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex-none p-4 border-t-gray-300 border-t w-full flex justify-end">
                            <button
                                className="text-sm rounded-full px-4 py-2 dynamic-color dynamic-bg"
                                style={{
                                    "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                    "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                    "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                    "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                    "--dynamic-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                    borderRadius: borderRadiusShorthand(layoutStyle),
                                } as React.CSSProperties}
                                onClick={handleApplyTags}
                            >
                                {t("product_list.filter_options.btn_filter")}
                            </button>
                        </div>
                    </div>
                )}
            </PopoverContent>
        )}
        </Popover>
    );
};

const TagFilterLoading = () => {
    const { t } = useTranslation();
    return (
        <div
            className={
                "bg-transparent rounded-full gap-1 py-[3px] px-4 h-fit  gap-x-2 justify-center border border-gray-400 flex items-center cursor-default focus:outline-none text-gray-400"
            }
        >
            {t("product_list.filter_options.product_tags")}
            <ChevronDown size={16} />
        </div>
    );
}

export default dynamic(() => Promise.resolve(CollectionListTagFilterClient), {
    ssr: false,
    loading: () => <TagFilterLoading />
});
